import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Skeleton,
  FormControl,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import {
  EmojiEvents as TrophyIcon,
  WorkspacePremium as MedalIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { selectIsAdmin } from "@/store/slices/authSlice";
import {
  useGetStudentRankingQuery,
  useGetAdminRankingQuery,
} from "@/store/api/homepageApi";

const getRankColor = (rank) => {
  switch (rank) {
    case 1:
      return "#FFD700";
    case 2:
      return "#C0C0C0";
    case 3:
      return "#CD7F32";
    default:
      return "#90A4AE";
  }
};

const getRankEmoji = (rank) => {
  switch (rank) {
    case 1:
      return "🥇";
    case 2:
      return "🥈";
    case 3:
      return "🥉";
    default:
      return null;
  }
};

const RankingRow = ({ ranking, isCurrentUser }) => {
  const studentName =
    ranking.student?.name ||
    `${ranking.student?.firstName || ""} ${ranking.student?.lastName || ""}`.trim() ||
    "Unknown";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1.5,
        px: 2,
        borderRadius: 2,
        background: isCurrentUser
          ? "linear-gradient(135deg, rgba(33,150,243,0.08) 0%, rgba(25,118,210,0.08) 100%)"
          : "transparent",
        border: isCurrentUser
          ? "1px solid rgba(33,150,243,0.2)"
          : "1px solid transparent",
        transition: "all 0.2s ease",
        "&:hover": {
          background: "rgba(0,0,0,0.02)",
        },
      }}
    >
      {/* Rank */}
      <Box
        sx={{
          minWidth: 32,
          height: 32,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            ranking.rank <= 3
              ? `${getRankColor(ranking.rank)}22`
              : "rgba(0,0,0,0.04)",
          fontWeight: 700,
          fontSize: ranking.rank <= 3 ? "1rem" : "0.8rem",
          color: ranking.rank <= 3 ? getRankColor(ranking.rank) : "#999",
        }}
      >
        {getRankEmoji(ranking.rank) || ranking.rank}
      </Box>

      {/* Avatar */}
      <Avatar
        src={ranking.student?.picture}
        sx={{
          width: 32,
          height: 32,
          fontSize: "0.75rem",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        {studentName.charAt(0)}
      </Avatar>

      {/* Name */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: isCurrentUser ? 700 : 500,
            color: isCurrentUser ? "#1976D2" : "#333",
            fontSize: "0.82rem",
            wordBreak: "break-word",
          }}
        >
          {studentName}
          {isCurrentUser && (
            <Typography
              component="span"
              variant="caption"
              sx={{ ml: 0.5, color: "#1976D2" }}
            >
              (You)
            </Typography>
          )}
        </Typography>
      </Box>

      {/* Score */}
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          color: ranking.rank <= 3 ? getRankColor(ranking.rank) : "#555",
          fontSize: "0.85rem",
        }}
      >
        {ranking.totalRankingScore}
      </Typography>
    </Box>
  );
};

const StudentRankingSidebar = () => {
  const { data, isLoading, error } = useGetStudentRankingQuery();

  if (error) {
    return (
      <Paper
        sx={{
          p: 3,
          borderRadius: 4,
          background: "white",
          boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <TrophyIcon sx={{ color: "#FFD700", fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#333" }}>
            Class Ranking
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: "#999", textAlign: "center", py: 3 }}>
          Ranking not available for your class yet.
        </Typography>
      </Paper>
    );
  }

  const rankingData = data?.data;
  const rankings = rankingData?.rankings || [];
  const classInfo = rankingData?.class;

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 4,
        background: "white",
        boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
        position: "sticky",
        top: 20,
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        <TrophyIcon sx={{ color: "#FFD700", fontSize: 28 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#333" }}>
          Class Ranking
        </Typography>
      </Box>

      {/* Class name */}
      {classInfo && (
        <Chip
          label={classInfo.className || classInfo.classCode}
          size="small"
          sx={{
            mb: 2,
            background: "rgba(33,150,243,0.08)",
            color: "#1976D2",
            fontWeight: 600,
            fontSize: "0.75rem",
          }}
        />
      )}

      {/* Rankings List */}
      {isLoading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rounded" height={48} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
      ) : rankings.length > 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {rankings.map((ranking) => (
            <RankingRow
              key={ranking.studentId}
              ranking={ranking}
              isCurrentUser={ranking.isCurrentUser}
            />
          ))}
        </Box>
      ) : (
        <Typography
          variant="body2"
          sx={{ color: "#999", textAlign: "center", py: 3 }}
        >
          No ranking data available yet.
        </Typography>
      )}
    </Paper>
  );
};

const AdminRankingSidebar = () => {
  const [selectedClassId, setSelectedClassId] = useState("");
  
  // Always get all classes data (without classId filter)
  const { data: allClassesData, isLoading: isLoadingClasses, error: classesError } = useGetAdminRankingQuery({});
  
  // Get rankings for selected class
  const { data: selectedClassData, isLoading: isLoadingRankings, error: rankingsError } = useGetAdminRankingQuery(
    selectedClassId ? { classId: selectedClassId } : {},
    { skip: !selectedClassId }
  );

  const allClasses = useMemo(() => {
    if (!allClassesData?.data?.classes) return [];
    return allClassesData.data.classes.map((c) => c.class);
  }, [allClassesData]);

  const displayRankings = useMemo(() => {
    if (!allClassesData?.data?.classes) return [];
    if (selectedClassId) {
      const found = allClassesData.data.classes.find(
        (c) => c.class._id === selectedClassId
      );
      return found ? found.rankings : [];
    }
    return allClassesData.data.classes.length > 0
      ? allClassesData.data.classes[0].rankings
      : [];
  }, [allClassesData, selectedClassId]);

  const selectedClass = useMemo(() => {
    if (!selectedClassId && allClasses.length > 0) return allClasses[0];
    return allClasses.find((c) => c._id === selectedClassId) || allClasses[0];
  }, [selectedClassId, allClasses]);

  const isLoading = isLoadingClasses || isLoadingRankings;
  const error = classesError || rankingsError;

  // Auto-select first class when data loads and no class is selected
  useEffect(() => {
    if (allClasses.length > 0 && !selectedClassId) {
      setSelectedClassId(allClasses[0]._id);
    }
  }, [allClasses, selectedClassId]);

  if (error) return null;

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 4,
        background: "white",
        boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
        position: "sticky",
        top: 20,
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <TrophyIcon sx={{ color: "#FFD700", fontSize: 28 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#333" }}>
          Student Ranking
        </Typography>
      </Box>

      {/* Class Selector - Always show dropdown for admins */}
      {allClasses.length > 0 && (
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <Select
            value={selectedClassId || (allClasses[0]?._id || "")}
            onChange={(e) => setSelectedClassId(e.target.value)}
            sx={{
              borderRadius: 2,
              fontSize: "0.85rem",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(33,150,243,0.2)",
              },
            }}
          >
            {allClasses.map((cls) => (
              <MenuItem key={cls._id} value={cls._id}>
                {cls.className || cls.classCode}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Show message when no classes available */}
      {allClasses.length === 0 && !isLoading && (
        <Chip
          label="No classes available"
          size="small"
          sx={{
            mb: 2,
            background: "rgba(244,67,54,0.08)",
            color: "#F44336",
            fontWeight: 600,
            fontSize: "0.75rem",
          }}
        />
      )}

      {/* Rankings List */}
      {isLoading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rounded" height={48} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
      ) : displayRankings.length > 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            maxHeight: 500,
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(0,0,0,0.1)",
              borderRadius: 2,
            },
          }}
        >
          {displayRankings.map((ranking) => (
            <RankingRow
              key={ranking.studentId}
              ranking={ranking}
              isCurrentUser={false}
            />
          ))}
        </Box>
      ) : (
        <Typography
          variant="body2"
          sx={{ color: "#999", textAlign: "center", py: 3 }}
        >
          No ranking data available yet.
        </Typography>
      )}
    </Paper>
  );
};

const RankingSidebar = () => {
  const isAdmin = useSelector(selectIsAdmin);

  return isAdmin ? <AdminRankingSidebar /> : <StudentRankingSidebar />;
};

export default RankingSidebar;
